import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Form } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { useStateMachine } from "little-state-machine";
import { useNavigate, useSearchParams } from "react-router-dom";

import "./OnBoardingForm.scss";
import { resetState } from "store/store";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import FormField from "components/molecules/FormField/FormField";
import { userActivation, getActivationUserDetail, getActivationGuestUserDetail, guestUserActivation } from "api";
import SplashLoader from "components/atoms/SplashLoader/SplashLoader";
import { languageOptions } from "pages/common/Settings/components/YourDetails/options";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { ReactComponent as DisableCheckIcon } from "assets/images/icons/DisableCheck.svg";
import { ReactComponent as CargobaseLogoIcon } from "assets/images/icons/CargobaseLogo.svg";
import testIds from "constants/testIds";
import useTimezoneSelectOptions from "hooks/useTimezoneSelectOptions";
import ActivationExpired from "./ActivationExpired";
import { IOnBoardingProps } from "./Types";

function OnBoardingForm(props: IOnBoardingProps) {
  const { setToken } = props;

  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shipmentId, setShipmentId] = useState("");

  const { actions } = useStateMachine({ resetState });
  const navigate = useNavigate();
  const intl = useIntl();
  const methods = useForm({ shouldFocusError: true });
  const { setValue, handleSubmit, watch } = methods;

  const timezoneData = useTimezoneSelectOptions();
  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");
  const hasLowerCase = newPassword && /[a-z]/.test(newPassword);
  const hasUpperCase = newPassword && /[A-Z]/.test(newPassword);
  const hasPasswordOneNumber = newPassword && /\d/.test(newPassword);
  const hasSpecialCharacter = newPassword && /[!@#$%^&*()_+{}[\]:;<>,.?~\\/-]/.test(newPassword);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email") || "";
  const encodedEmailParam = encodeURIComponent(emailParam);
  const currentLocation = location.pathname;

  useEffect(() => {
    setIsLoading(true);
    if (currentLocation === "/guest-signup/verify-activation") {
      getActivationGuestUserDetail(token, encodedEmailParam)
        .then((res) => {
          setIsLoading(false);
          setIsExpired(false);
          const { email_id, shipment } = res.data;
          setValue("email", email_id);
          setShipmentId(shipment);
        })
        .catch(() => {
          setIsLoading(false);
          setIsExpired(true);
        });
    } else {
      getActivationUserDetail(token, encodedEmailParam)
        .then((res) => {
          setIsLoading(false);
          setIsExpired(false);
          const { email, first_name, last_name, language, phone_no, preferred_timezone, unit_system_preferences } = res.data;
          setValue("email", email);
          setValue("first_name", first_name);
          setValue("last_name", last_name);
          setValue("phone_no", phone_no);
          setValue("preferred_timezone", preferred_timezone);
          setValue("language", language);
          setValue("unit_system_preferences", unit_system_preferences);
        })
        .catch(() => {
          setIsLoading(false);
          setIsExpired(true);
        });
    }
  }, []);

  const onSubmit = (data: any) => {
    const { first_name, last_name, phone_no, preferred_timezone, unit_system_preferences, language, company_name } = data;
    const payload = {
      token,
      email: emailParam,
      first_name,
      last_name,
      phone_no,
      preferred_timezone,
      unit_system_preferences,
      language,
      password: newPassword,
    };
    if (
      hasLowerCase &&
      hasUpperCase &&
      hasPasswordOneNumber &&
      newPassword &&
      confirmPassword &&
      newPassword === confirmPassword &&
      newPassword?.length >= 8
    ) {
      setIsLoading(true);
      // Reset store data
      actions.resetState();
      // Remove any pre-existing permissions
      localStorage.removeItem("cargobase_userpermissions");

      if (currentLocation === "/guest-signup/verify-activation") {
        guestUserActivation({ ...payload, company_name })
          .then((res) => {
            setIsLoading(false);
            setToken(res.data.token);
            if (res.data.token) {
              navigate(`/shipment/${shipmentId}`);
            }
          })
          .catch((err) => {
            setIsLoading(false);
            errorToast(err);
          });
      } else {
        userActivation(payload)
          .then((res) => {
            setIsLoading(false);
            // Cookies.set("cargobase_logged_in_as_token", res.data.token);
            // setToken(res.data.token);
            if (res.data.token) {
              // setIsLoggedInAs(true);
              navigate("/");
            }
          })
          .catch((err) => {
            setIsLoading(false);
            errorToast(err);
          });
      }
    }
  };

  if (isLoading) return <SplashLoader />;

  if (isExpired) {
    return <ActivationExpired />;
  }

  return (
    <FormProvider {...methods}>
      <div className="h-100 d-flex align-items-center justify-content-center position-relative z-index-1">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="card-wrapper p-18">
            <div>
              <CargobaseLogoIcon />
            </div>
            <div>
              <h4 className="mt-42">
                <FormattedMessage id="onboarding.account" defaultMessage="Create Account" />
              </h4>
              <p className="mt-3">
                <span>
                  <FormattedMessage
                    id="onboarding.title"
                    defaultMessage="Welcome to Cargobase. Please provide or verify the information below, and activate the account. If you have any questions, do not hesitate to contact Support at"
                  />
                </span>{" "}
                <span>
                  <a href="mailto:support@cargobase.com">support@cargobase.com</a>
                </span>
              </p>
            </div>
            <div className="flex-row mt-18">
              <h6 className="w-158">
                <FormattedMessage id="settings.your-details.contactDetails" defaultMessage="Contact details" />
              </h6>
              <div className="w-296 h-30">
                <FormField
                  id="first_name"
                  label={intl.formatMessage({
                    id: "settings.contact-list.firstName",
                    defaultMessage: "First Name",
                  })}
                  mandatory
                  eventContext="User Activation Form"
                  type="textinput"
                  placeholder={intl.formatMessage({
                    id: "settings.contact-list.firstName",
                    defaultMessage: "First Name",
                  })}
                  field_id="first_name"
                  onChange={(e) => setValue("first_name", e.target.value)}
                />
              </div>
              <div className="w-296 h-30">
                <FormField
                  id="last_name"
                  label={intl.formatMessage({
                    id: "settings.contact-list.lastName",
                    defaultMessage: "Last Name",
                  })}
                  mandatory
                  eventContext="User Activation Form"
                  type="textinput"
                  placeholder={intl.formatMessage({
                    id: "settings.contact-list.lastName",
                    defaultMessage: "Last Name",
                  })}
                  field_id="last_name"
                  onChange={(e) => setValue("last_name", e.target.value)}
                />
              </div>
            </div>
            <div className="flex-row mt-18">
              <label className="w-158" />
              <div className="w-296 h-30">
                <FormField
                  id="email"
                  label={intl.formatMessage({
                    id: "settings.contact-list.email.placeholder",
                    defaultMessage: "Email",
                  })}
                  mandatory
                  eventContext="User Activation Form"
                  type="textinput"
                  placeholder={intl.formatMessage({
                    id: "settings.contact-list.email.placeholder",
                    defaultMessage: "Email",
                  })}
                  field_id="email"
                  onChange={(e) => setValue("email", e.target.value)}
                  disabled
                />
              </div>
              <div className="w-296 h-30">
                <FormField
                  id="phone"
                  label={intl.formatMessage({
                    id: "new-request.schedule.pickupContactPhone",
                    defaultMessage: "Phone",
                  })}
                  mandatory
                  eventContext="User Activation Form"
                  type="textinput"
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.pickupContactPhone",
                    defaultMessage: "Phone",
                  })}
                  field_id="phone_no"
                  onChange={(e) => setValue("phone_no", e.target.value)}
                />
              </div>
            </div>
            <div className="flex-row mt-5">
              <h6 className="w-158">
                <FormattedMessage id="settings.your-details.userPreference" defaultMessage="User Preference" />
              </h6>
              <div className="w-192 h-30">
                <div className="required">
                  <FormattedMessage id="new-request.schedule.addressBookDialog.timezone" defaultMessage="Timezone" />
                </div>
                <div className="mt-2">
                  <SearchSelect options={timezoneData} name="preferred_timezone" placeholder="Please select" />
                </div>
              </div>
              <div className="w-192 h-30">
                <div className="required">
                  <FormattedMessage id="settings.your-details.unitSystem" defaultMessage="Unit System" />
                </div>
                <div className="mt-2">
                  <Dropdown
                    useHookForm
                    required
                    placeholder="Please select"
                    name="unit_system_preferences"
                    options={[
                      { value: "cm-kg", label: "Cm - Kg" },
                      { value: "inch-lbs", label: "Inch - Lbs" },
                    ]}
                  />
                </div>
              </div>
              <div className="w-192 h-30">
                <div className="required">
                  <FormattedMessage id="settings.team-members.preferredlanguage" defaultMessage="Preferred Language" />
                </div>
                <div className="mt-2">
                  <Dropdown useHookForm required placeholder="Please select" name="language" options={languageOptions} />
                </div>
              </div>
            </div>
            <div className="flex-row mt-18">
              <h6 className="w-158">
                <FormattedMessage id="general.password" defaultMessage="Password" />
              </h6>
              <div className="w-296 h-30">
                <div>
                  <FormField
                    id="newPassword"
                    label={intl.formatMessage({
                      id: "general.newPassword",
                      defaultMessage: "New Password",
                    })}
                    mandatory
                    eventContext="User Activation Form"
                    type="password"
                    placeholder={intl.formatMessage({
                      id: "general.newPassword",
                      defaultMessage: "New Password",
                    })}
                    field_id="newPassword"
                    onChange={(e) => setValue("newPassword", e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <FormField
                    id="confirmPassword"
                    label={intl.formatMessage({
                      id: "general.confirmPassword",
                      defaultMessage: "Confirm Password",
                    })}
                    mandatory
                    eventContext="User Activation Form"
                    type="password"
                    placeholder={intl.formatMessage({
                      id: "general.confirmPassword",
                      defaultMessage: "Confirm Password",
                    })}
                    field_id="confirmPassword"
                    onChange={(e) => setValue("confirmPassword", e.target.value)}
                  />
                </div>
                {currentLocation === "/guest-signup/verify-activation" && (
                  <div className="w-296 h-30 mt-4">
                    <FormField
                      id="company_name"
                      label={intl.formatMessage({
                        id: "general.companyName",
                        defaultMessage: "Company Name",
                      })}
                      eventContext="Guest User Activation Form"
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "general.companyName",
                        defaultMessage: "Company Name",
                      })}
                      field_id="company_name"
                      onChange={(e) => setValue("company_name", e.target.value)}
                    />
                  </div>
                )}
              </div>
              <div className="ml-5">
                <p>
                  <FormattedMessage id="onboarding.passwordCriteria" defaultMessage="Password Criteria" />
                </p>
                <p className="d-flex flex-column">
                  <span className="mt-1">
                    {newPassword?.length >= 8 ? (
                      <CheckIcon />
                    ) : !newPassword?.length ? (
                      <DisableCheckIcon />
                    ) : (
                      <ExclamationIcon />
                    )}{" "}
                    <FormattedMessage id="onboarding.characterValidation" defaultMessage="8 characters or more" />
                  </span>
                  <span className="mt-1">
                    {hasLowerCase ? <CheckIcon /> : !newPassword?.length ? <DisableCheckIcon /> : <ExclamationIcon />}{" "}
                    <FormattedMessage
                      id="onboarding.leastOneLowerCaseCharacter"
                      defaultMessage="At least one lower case letter (a-z)"
                    />
                  </span>
                  <span className="mt-1">
                    {hasUpperCase ? <CheckIcon /> : !newPassword?.length ? <DisableCheckIcon /> : <ExclamationIcon />}{" "}
                    <FormattedMessage
                      id="onboarding.leastOneUpperCaseCharacter"
                      defaultMessage="At least one upper case letter (A-Z)"
                    />
                  </span>
                  <span className="mt-1">
                    {hasSpecialCharacter ? <CheckIcon /> : !newPassword?.length ? <DisableCheckIcon /> : <ExclamationIcon />}{" "}
                    <FormattedMessage
                      id="onboarding.leastOneSpecialCharacter"
                      defaultMessage="At least one special character ( !@#$%^&* )"
                    />
                  </span>
                  <span className="mt-1">
                    {hasPasswordOneNumber ? (
                      <CheckIcon />
                    ) : !newPassword?.length ? (
                      <DisableCheckIcon />
                    ) : (
                      <ExclamationIcon />
                    )}{" "}
                    <FormattedMessage id="onboarding.atLeastOneNumber" defaultMessage="At least one number" />
                  </span>
                  <span className="mt-1">
                    {newPassword && confirmPassword && newPassword === confirmPassword ? (
                      <CheckIcon />
                    ) : !confirmPassword?.length ? (
                      <DisableCheckIcon />
                    ) : (
                      <ExclamationIcon />
                    )}{" "}
                    <FormattedMessage id="onboarding.passwordIdentical" defaultMessage="Passwords are identical" />
                  </span>
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Button
                className="rounded-pill ml-auto font-b1 btn-size"
                label={intl.formatMessage({
                  id: "onboarding.activateAccount",
                  defaultMessage: "Activate Account",
                })}
                type="submit"
                isLoading={isLoading}
                data-testid={testIds.ACTIVATE_ACCOUNT_BUTTON}
              />
            </div>
          </div>
        </Form>
      </div>
    </FormProvider>
  );
}

export default OnBoardingForm;
